import React, { useEffect, useState } from "react";
import { KeyRound, Save } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import { Btn, Field, Modal, inputCls } from "./kit";

export const LOGIN_PIN_CHANGED = "accounts:login-pin-changed";
export const canSetLoginPin = (u) => !!u && u.role !== "partner_user" && u.role !== "resident" && (u.role === "super_admin" || (u.staff_permissions || []).includes("set_login_pin"));
const pinOnly = (v) => v.replace(/\D/g, "").slice(0, 6);

/** PIN column cell: Set / Not set / Google sign-in + last changed. Never the PIN. */
export const PinStatus = ({ row, testId }) => (
  <div data-testid={testId}>
    <span className={row.has_pin ? "text-[#d4a017]" : "text-white/40"}>{row.pin_label || (row.has_pin ? "Set" : "Not set")}</span>
    {row.pin_changed_at && <div className="text-[10px] text-white/40">changed {new Date(row.pin_changed_at).toLocaleDateString()}</div>}
  </div>
);

/** One dialog for staff, partner and resident accounts — all call POST /accounts/login-pin. */
export const LoginPinDialog = ({ kind, id, name, login: login0, source, onClose }) => {
  const [login, setLogin] = useState(login0 || "");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    if (kind === "resident" && !login0) apiClient.get(`/accounts/login-pin/resident/${id}`).then(({ data }) => setLogin((cur) => cur || data.login || "")).catch(() => {});
  }, [kind, id, login0]);
  const mismatch = pin2.length === 6 && pin !== pin2;
  const ok = login.trim() && pin.length === 6 && pin === pin2;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/accounts/login-pin", { kind, id, login, pin, pin_confirm: pin2, source });
      window.dispatchEvent(new CustomEvent(LOGIN_PIN_CHANGED, { detail: data }));
      window.dispatchEvent(new Event("problems:changed"));
      setPin(""); setPin2("");
      if (data.notice) { setNotice(data.notice); toast.success("PIN saved"); } else { toast.success("Login & PIN saved"); onClose(); }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Set login & PIN · ${name}`} testId="login-pin-dialog">
      {notice ? (
        <div className="space-y-4">
          <p className="text-sm text-amber-300" data-testid="login-pin-notice">{notice}</p>
          <div className="flex justify-end"><Btn data-testid="login-pin-done" onClick={onClose}>Done</Btn></div>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label={["resident", "contract_vendor"].includes(kind) ? "Login (mobile number)" : "Login (email)"}>
            <input data-testid="login-pin-login" className={inputCls} value={login} onChange={(e) => setLogin(e.target.value)} placeholder={["resident", "contract_vendor"].includes(kind) ? "(843) 555-0123" : "name@example.org"} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="New PIN (6 digits)"><input data-testid="login-pin-pin" type="password" inputMode="numeric" autoComplete="new-password" className={inputCls} value={pin} onChange={(e) => setPin(pinOnly(e.target.value))} /></Field>
            <Field label="Confirm PIN"><input data-testid="login-pin-confirm" type="password" inputMode="numeric" autoComplete="new-password" className={inputCls} value={pin2} onChange={(e) => setPin2(pinOnly(e.target.value))} /></Field>
          </div>
          {mismatch && <p className="text-xs text-red-300" data-testid="login-pin-mismatch">The two PINs don't match.</p>}
          {kind === "partner" && <p className="text-[11px] text-white/50">Pending partners keep their status and still can't sign in until approved.</p>}
          <div className="flex justify-end gap-2">
            <Btn tone="muted" data-testid="login-pin-cancel" onClick={onClose}>Cancel</Btn>
            <Btn data-testid="login-pin-save" disabled={!ok || busy} onClick={save}><Save size={12} /> Save</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
};

/** Row button. Hidden for anyone without set_login_pin; disabled (with reason) for archived / exited residents. */
export const LoginPinButton = ({ kind, id, name, login, status, source, className = "", note = false }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!canSetLoginPin(user)) return null;
  const blocked = kind === "resident" && ["archived", "exited"].includes(status);
  return (
    <>
      <Btn tone="muted" className={className} disabled={blocked} title={blocked ? `${status === "archived" ? "Archived" : "Exited"} residents can't get a new PIN` : undefined}
        data-testid={`login-pin-btn-${kind}-${id}`} onClick={() => setOpen(true)}><KeyRound size={12} /> Set login &amp; PIN</Btn>
      {blocked && note && <div className="text-[10px] text-white/40 mt-1" data-testid={`login-pin-blocked-${id}`}>{status === "archived" ? "Archived" : "Exited"} residents can't get a new PIN</div>}
      {open && <LoginPinDialog kind={kind} id={id} name={name} login={login} source={source} onClose={() => setOpen(false)} />}
    </>
  );
};

export const useLoginPinRefresh = (fn) => useEffect(() => { window.addEventListener(LOGIN_PIN_CHANGED, fn); return () => window.removeEventListener(LOGIN_PIN_CHANGED, fn); });
